// Archivo para manejar la creación masiva de consumidores
//  ⓘ  Cambios clave:
//    1.  Ya no “adivinamos” full_name/phone/email.
//    2.  Tomamos del layout los campos con role === 'full_name' | 'phone' | 'email'
//        y los enviamos al backend en esas columnas.
// -----------------------------------------------------------------------------
import React, { useState, useEffect } from 'react'
import {
  Container,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Box,
  Button,
  MenuItem,
  TextField,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  LinearProgress,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material'
import * as XLSX from 'xlsx'

// api = instancia axios (default export)
// me = función exportada desde services/api.js
import api, { me } from '../../services/api'
import { getLayoutsByProduct } from '../../services/layoutService'
import { getActiveLayout, parseLayoutConfig } from '../../utils/layoutUtils'

// Ajusta el import si tu ConsumerServices está en otra ruta
import {
  getClients,
  getProductsByClient,
} from '../../services/ConsumerServices'

const steps = [
  'Seleccionar producto',
  'Cargar archivo',
  'Validar datos',
  'Confirmar y enviar',
]

export default function ConsumerBulkUpload() {
  const [activeStep, setActiveStep] = useState(0)

  // Roles / modo
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false)

  // Clientes (solo globalAdmin)
  const [clients, setClients] = useState([])
  const [selectedClient, setSelectedClient] = useState('')
  const [loadingClients, setLoadingClients] = useState(false)

  // Productos
  const [products, setProducts] = useState([])
  const [loadingProducts, setLoadingProducts] = useState(false)
  const [selectedProduct, setSelectedProduct] = useState('')

  // Layout + archivo + filas
  const [layoutConfig, setLayoutConfig] = useState([])
  const [file, setFile] = useState(null)
  const [rows, setRows] = useState([])
  const [fileMeta, setFileMeta] = useState({})

  // UI
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [snack, setSnack] = useState({
    open: false,
    message: '',
    severity: 'info',
  })

  /* ────────────────────────────────────────────
   * 1) Determinar rol y precargar datos iniciales
   * ──────────────────────────────────────────── */
  useEffect(() => {
    const init = async () => {
      try {
        const user = await me()

        // user.roles puede venir como array de strings o array de objetos (según tu backend)
        const rolesArr = Array.isArray(user?.roles) ? user.roles : []
        const hasGlobal = rolesArr.some((r) => {
          const type = typeof r === 'string' ? r : r?.type
          const name = typeof r === 'string' ? r : r?.name_role
          return type === 'globalAdmin' || name === 'globalAdmin'
        })

        setIsGlobalAdmin(hasGlobal)

        // Si es globalAdmin: cargar clientes y esperar selección
        if (hasGlobal) {
          setLoadingClients(true)
          try {
            const cl = await getClients()
            setClients(Array.isArray(cl) ? cl : [])
          } catch (e) {
            console.error('[BulkUpload] Error al cargar clientes:', e)
            setClients([])
            setSnack({
              open: true,
              message: 'Error al cargar clientes.',
              severity: 'error',
            })
          } finally {
            setLoadingClients(false)
          }
          setProducts([]) // hasta elegir cliente
          return
        }

        // Si NO es globalAdmin: cargar productos “por token”
        setLoadingProducts(true)
        try {
          const pr = await getProductsByClient()
          setProducts(Array.isArray(pr) ? pr : [])
        } catch (e) {
          console.error('[BulkUpload] Error al cargar productos:', e)
          setProducts([])
          setSnack({
            open: true,
            message: 'Error al cargar productos',
            severity: 'error',
          })
        } finally {
          setLoadingProducts(false)
        }
      } catch (e) {
        console.error('[BulkUpload] Error en init (me):', e)
        setSnack({
          open: true,
          message: 'No se pudo validar sesión (me).',
          severity: 'error',
        })
      }
    }

    init()
  }, [])

  /* ────────────────────────────────────────────
   * 2) Si es globalAdmin y cambia el cliente -> cargar productos de ese cliente
   * ──────────────────────────────────────────── */
  useEffect(() => {
    const loadProductsForClient = async () => {
      if (!isGlobalAdmin) return
      if (!selectedClient) {
        setProducts([])
        setSelectedProduct('')
        return
      }

      setLoadingProducts(true)
      try {
        const pr = await getProductsByClient(selectedClient)
        setProducts(Array.isArray(pr) ? pr : [])
        setSelectedProduct('') // fuerza selección de producto coherente con nuevo cliente
      } catch (e) {
        console.error('[BulkUpload] Error al cargar productos por cliente:', e)
        setProducts([])
        setSnack({
          open: true,
          message: 'Error al cargar productos del cliente seleccionado.',
          severity: 'error',
        })
      } finally {
        setLoadingProducts(false)
      }
    }

    loadProductsForClient()
  }, [isGlobalAdmin, selectedClient])

  /* ────────────────────────────────────────────
   * 3) Reset de layout cuando cambia producto
   * ──────────────────────────────────────────── */
  useEffect(() => {
    setLayoutConfig([])
    // opcional: si cambias de producto, limpia archivo/preview para evitar inconsistencias
    setFile(null)
    setRows([])
    setFileMeta({})
    setProgress(0)
  }, [selectedProduct])

  /* obtener layout del producto seleccionado */
  const fetchLayout = async () => {
    if (!selectedProduct) return

    const key = `layout_${selectedProduct}`
    const cached = sessionStorage.getItem(key)
    if (cached) {
      setLayoutConfig(JSON.parse(cached))
      return
    }

    try {
      const layoutData = await getLayoutsByProduct(selectedProduct)
      const payload = layoutData?.data ?? layoutData
      const layouts = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.layouts)
          ? payload.layouts
          : payload
            ? [payload]
            : []

      const activeLayout = getActiveLayout(layouts)
      const parsedConfig = parseLayoutConfig(
        activeLayout?.config ?? activeLayout?.layoutConfig ?? activeLayout?.fields
      )

      if (parsedConfig.length > 0) {
        sessionStorage.setItem(key, JSON.stringify(parsedConfig))
        setLayoutConfig(parsedConfig)
      } else {
        throw new Error('Layout vacío o inválido')
      }
    } catch (err) {
      console.error('[BulkUpload] Error al obtener layout:', err)
      setSnack({
        open: true,
        message: 'Este producto no tiene layout',
        severity: 'warning',
      })
      setLayoutConfig([])
    }
  }

  /* ─────────────── Paso 2: leer archivo para vista previa ─────────────── */
  const handleFile = (e) => {
    const f = e.target.files[0]
    if (!f) return
    setFile(f)
    setFileMeta({ name: f.name, sizeKB: (f.size / 1024).toFixed(2) })
    const ext = f.name.split('.').pop().toLowerCase()
    const reader = new FileReader()
    reader.onload = (ev) => {
      const wb =
        ext === 'csv'
          ? XLSX.read(ev.target.result, { type: 'binary' })
          : XLSX.read(new Uint8Array(ev.target.result), { type: 'array' })
      const sheet = wb.Sheets[wb.SheetNames[0]]
      setRows(XLSX.utils.sheet_to_json(sheet, { defval: '' }))
    }
    ext === 'csv' ? reader.readAsBinaryString(f) : reader.readAsArrayBuffer(f)
  }

  /* ───────────── Validación: columnas del layout ───────────── */
  const normalize = (s) =>
    String(s || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9_]+/g, '_')

  const handleValidate = () => {
    if (!Array.isArray(layoutConfig) || layoutConfig.length === 0) {
      setSnack({
        open: true,
        message: 'No hay layout cargado para validar.',
        severity: 'error',
      })
      return false
    }

    if (!rows.length) {
      setSnack({
        open: true,
        message: 'El archivo no contiene filas para validar.',
        severity: 'error',
      })
      return false
    }

    const need = layoutConfig.map((f) => normalize(f.name))
    const got = Object.keys(rows[0] || {}).map(normalize)
    const miss = need.filter((k) => !got.includes(k))
    if (miss.length) {
      setSnack({
        open: true,
        message: `Faltan columnas: ${miss.join(', ')}`,
        severity: 'error',
      })
      return false
    }
    return true
  }

  /* ─────────────── Paso 4: subir al backend ─────────────── */
  const handleUpload = async () => {
    if (!file) return
    if (!selectedProduct) {
      setSnack({
        open: true,
        message: 'Selecciona un producto antes de subir.',
        severity: 'warning',
      })
      return
    }

    setLoading(true)
    setProgress(0)
    try {
      const form = new FormData()
      form.append('file', file)
      form.append('layoutConfig', JSON.stringify(layoutConfig))
      form.append('product_id', String(selectedProduct))

      // opcional: útil para auditoría / trazabilidad, aunque tu backend puede ignorarlo
      if (isGlobalAdmin && selectedClient) {
        form.append('client_id', String(selectedClient))
      }

      const { data } = await api.post('/consumers/bulk-upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => {
          if (!e.total) return
          setProgress(Math.round((e.loaded * 100) / e.total))
        },
      })

      setSnack({
        open: true,
        message: `Insertados: ${data.inserted}/${data.total}`,
        severity: 'success',
      })
    } catch (err) {
      console.error('[BulkUpload] Error al subir archivo:', err)
      setSnack({
        open: true,
        message:
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          'Error al subir archivo',
        severity: 'error',
      })
    } finally {
      setLoading(false)
      setTimeout(() => {
        setActiveStep(0)
        setRows([])
        setFile(null)
        setProgress(0)
        setFileMeta({})
        setLayoutConfig([])
        // NO limpiamos cliente/producto automáticamente: depende de tu UX;
        // si quieres reiniciar todo, descomenta:
        // setSelectedProduct('')
        // setSelectedClient('')
      }, 2000)
    }
  }

  /* ─────────────── Render ─────────────── */
  const nextDisabled =
    loading ||
    (activeStep === 0 &&
      (isGlobalAdmin ? !selectedClient || !selectedProduct : !selectedProduct))

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        Carga Masiva de Consumidores
      </Typography>

      <Stepper activeStep={activeStep} sx={{ my: 4 }}>
        {steps.map((l) => (
          <Step key={l}>
            <StepLabel>{l}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* paso 0 */}
      {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Dropdown de clientes SOLO para globalAdmin */}
          {isGlobalAdmin && (
            <>
              {loadingClients ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : (
                <TextField
                  select
                  fullWidth
                  label="Selecciona un cliente"
                  value={selectedClient}
                  onChange={(e) => setSelectedClient(e.target.value)}
                  disabled={clients.length === 0}
                >
                  {clients.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name_client || c.name || `Cliente ${c.id}`}
                    </MenuItem>
                  ))}
                </TextField>
              )}

              {clients.length === 0 && !loadingClients && (
                <Alert severity="warning">
                  No hay clientes disponibles para tu cuenta.
                </Alert>
              )}
            </>
          )}

          {/* Dropdown de productos */}
          {loadingProducts ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <TextField
              select
              fullWidth
              label="Selecciona un producto"
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
              disabled={
                products.length === 0 || (isGlobalAdmin && !selectedClient)
              }
            >
              {products.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name_product}
                </MenuItem>
              ))}
            </TextField>
          )}

          {products.length === 0 && !loadingProducts && (
            <Alert severity="warning">
              No hay productos disponibles para el contexto actual.
            </Alert>
          )}
        </Box>
      )}

      {/* paso 1 */}
      {activeStep === 1 && (
        <Box>
          <Button variant="outlined" component="label" disabled={loading}>
            Seleccionar archivo (.csv/.xlsx)
            <input
              hidden
              type="file"
              accept=".csv,.xlsx"
              onChange={handleFile}
            />
          </Button>
          {fileMeta.name && (
            <Typography mt={2}>
              {fileMeta.name} — {fileMeta.sizeKB} KB
            </Typography>
          )}
        </Box>
      )}

      {/* paso 2: preview */}
      {activeStep === 2 && (
        <Box>
          <Typography gutterBottom>
            Vista previa ({rows.length} filas, primeras 5)
          </Typography>
          <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {Object.keys(rows[0] || {}).map((c) => (
                    <TableCell key={c}>{c}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, 5).map((r, i) => (
                  <TableRow key={i}>
                    {Object.values(r).map((v, j) => (
                      <TableCell key={j}>{v}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* paso 3 */}
      {activeStep === 3 &&
        (loading ? (
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ my: 2 }}
          />
        ) : (
          <Typography mt={2}>
            Listo para enviar {rows.length} registros.
          </Typography>
        ))}

      {/* controles */}
      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          disabled={activeStep === 0 || loading}
          onClick={() => setActiveStep((s) => s - 1)}
        >
          Atrás
        </Button>

        {activeStep < steps.length - 1 ? (
          <Button
            disabled={nextDisabled}
            onClick={async () => {
              if (activeStep === 0) await fetchLayout()
              if (activeStep === 2 && !handleValidate()) return
              setActiveStep((s) => s + 1)
            }}
          >
            Siguiente
          </Button>
        ) : (
          <Button variant="contained" disabled={loading} onClick={handleUpload}>
            Subir consumidores
          </Button>
        )}
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snack.severity}>{snack.message}</Alert>
      </Snackbar>
    </Container>
  )
}
